import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { useMemo, useEffect, useCallback } from 'react';
import throttle from 'lodash/throttle';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { denormalize } from 'normalizr';
import { useOnScrollNearBottom } from '@wix/da-hooks/pkg/useOnScrollNearBottom';
import { selectors as streamSelectors, actions as streamActions } from '../../Stream';
import { createGetDeviationsByIds } from '../../redux/deviations/selectors';
import { createGetDenormalizedDataEntitiesByIds, getDataEntities } from '../../redux/entities/selectors';
import { galleryFolderSchema, collectionFolderSchema } from '../../redux/normalizr/schemas/folder';
import { createGetUsersByIds } from '../../redux/users/selectors';
import { initializeIfNeeded } from '../../Stream/base/actions';
export function useStream(streamId, throttleTimeout = 500, initConfig) {
    const dispatch = useDispatch();
    useEffect(()=>{
        if (!initConfig) {
            return;
        }
        dispatch(initializeIfNeeded(streamId, initConfig));
    }, [
        streamId,
        initConfig,
        dispatch
    ]);
    const stream = useSelector((state)=>({
            items: streamSelectors.getItems(state, streamId),
            total: streamSelectors.getTotal(state, streamId),
            hasMore: streamSelectors.hasMore(state, streamId),
            hasLess: streamSelectors.getHasLess(state, streamId),
            isFetching: streamSelectors.isFetching(state, streamId),
            isInitialized: streamSelectors.isInitialized(state, streamId),
            errorMessage: streamSelectors.getErrorMsg(state, streamId)
        }), shallowEqual);
    const fetchNext = useMemo(()=>{
        return throttle(()=>{
            dispatch(streamActions.fetchNext(streamId));
        }, throttleTimeout);
    }, [
        dispatch,
        streamId,
        throttleTimeout
    ]);
    const fetchPrevious = useMemo(()=>{
        return throttle(()=>{
            dispatch(streamActions.fetchPrevious(streamId));
        }, throttleTimeout);
    }, [
        dispatch,
        streamId,
        throttleTimeout
    ]);
    const fetchAll = useMemo(()=>{
        return throttle(()=>{
            dispatch(streamActions.fetchAll(streamId));
        }, throttleTimeout);
    }, [
        dispatch,
        streamId,
        throttleTimeout
    ]);
    const fetchRetry = useMemo(()=>{
        return throttle(()=>{
            dispatch(streamActions.fetchRetry(streamId));
        }, throttleTimeout);
    }, [
        dispatch,
        streamId,
        throttleTimeout
    ]);
    const cancelFetch = useCallback(()=>dispatch(streamActions.cancelFetchForStream(streamId)), [
        dispatch,
        streamId
    ]);
    useEffect(()=>{
        return ()=>{
            fetchNext.cancel();
            fetchPrevious.cancel();
            fetchAll.cancel();
            fetchRetry.cancel();
        };
    }, [
        fetchNext,
        fetchPrevious,
        fetchAll,
        fetchRetry
    ]);
    return _object_spread_props(_object_spread({}, stream), {
        streamId,
        fetchNext,
        fetchPrevious,
        fetchAll,
        fetchRetry,
        cancelFetch
    });
}
export function useNormalizedStream(streamId, schema, throttleTimeout = 500, initConfig) {
    const _useStream = useStream(streamId, throttleTimeout, initConfig), { items } = _useStream, restStream = _object_without_properties(_useStream, [
        "items"
    ]);
    const entities = useSelector(getDataEntities);
    const denormalizedItems = denormalize(items, schema, entities);
    return _object_spread_props(_object_spread({}, restStream), {
        items: denormalizedItems
    });
}
export function useNormalizedDeviationStream(streamId, throttleTimeout = 500, initConfig) {
    const _useStream = useStream(streamId, throttleTimeout, initConfig), { items } = _useStream, restStream = _object_without_properties(_useStream, [
        "items"
    ]);
    const getDeviationsByIds = useMemo(()=>createGetDeviationsByIds(), []);
    const deviations = useSelector((state)=>getDeviationsByIds(state, items));
    return _object_spread_props(_object_spread({}, restStream), {
        items: deviations
    });
}
export function useNormalizedUserStream(streamId, throttleTimeout = 500, initConfig) {
    const _useStream = useStream(streamId, throttleTimeout, initConfig), { items } = _useStream, restStream = _object_without_properties(_useStream, [
        "items"
    ]);
    const getUsersByIds = useMemo(()=>createGetUsersByIds(), []);
    const users = useSelector((state)=>getUsersByIds(state, items));
    return _object_spread_props(_object_spread({}, restStream), {
        items: users
    });
}
export function useNormalizedFolderStream(streamId, gallectionType) {
    const _useStream = useStream(streamId), { items } = _useStream, restStream = _object_without_properties(_useStream, [
        "items"
    ]);
    const getDenormalizedDataEntitiesByIds = useMemo(()=>createGetDenormalizedDataEntitiesByIds(), []);
    const folders = useSelector((state)=>getDenormalizedDataEntitiesByIds(state, gallectionType === 'gallery' ? galleryFolderSchema : collectionFolderSchema, items));
    return _object_spread_props(_object_spread({}, restStream), {
        items: folders
    });
}
/**
 * Creates an `onScroll` callback. Pass the returned callback to your scrollable area's `onScroll` prop. Then, whenever the user scrolls near the bottom
 * of the scrollable area, this will trigger a fetch.
 *
 * @param streamInfo The output of a `useStream` hook.
 * @param fetchDistance How far from the bottom should we trigger the fetch?
 */ export function useFetchOnScrollNearBottom(streamInfo, fetchDistance) {
    const { streamId, isFetching, hasMore, fetchNext } = streamInfo;
    return useOnScrollNearBottom(fetchDistance, useCallback(()=>{
        if (!streamId || isFetching || !hasMore) {
            return;
        }
        fetchNext();
    }, [
        streamId,
        isFetching,
        hasMore,
        fetchNext
    ]));
}
